import { useState, useEffect, useRef } from 'react';
import { getMaskedValue, useUrlFromValue } from 'components/Inputs/helpers';
import { ViewWrapper, LinkButtonWrapper } from './styles';
import { useKeyBoardContext } from 'hooks/keyboardEventHandler/useKeyBoardContext';
import Validation from 'components/Inputs/Validation';
import { getOriginalError } from 'services/AxiosService';
import { useFlashTransition } from 'hooks/useFlashState';
import { Button } from '@kizen/kds/Button';
import { InlineMarkdownText } from 'components/WYSIWYG/MarkdownEditor/components/InlineMarkdownText';
import IconAdornment from 'components/Inputs/Adornments/IconAdornment';
import { getRawTextValue } from 'components/WYSIWYG/MarkdownEditor/helpers';

export default function LongTextInline({
  value = '',
  onSubmit,
  autoFocus = false,
  onAutoFocus = null,
  emptyPlaceholder = '—',
  readOnly = false,
  enableResize = false,
  tooltipLabel = '',
  popperConfig,
  contentWidth,
  'data-field-type': qaType,
  'data-field-id': qaId,
  field,
  entityId,
  shouldFocusNext,
  displayMaskedValue,
  keyListenerId,
  inModal = false,
  fullTooltip = false,
  ...others
}) {
  const maskedValueEnabled = displayMaskedValue && field?.meta?.isMasked;
  const processedValue = maskedValueEnabled
    ? getMaskedValue(value)
    : value ?? '';
  const fieldId = keyListenerId || `${field?.id}-${entityId}`;
  const isMarkdown = field?.meta?.isMarkdown ?? false;
  const [editing, setEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [stagedValue, setStagedValue] = useState(processedValue ?? '');

  useEffect(() => {
    setStagedValue(value ?? '');
  }, [value, editing]);

  const overlayTarget = useRef(null);
  const { assignFieldHandle, getKeyListenersProps } = useKeyBoardContext();

  const onAutoFocusRef = useRef();
  onAutoFocusRef.current = onAutoFocus;

  useEffect(() => {
    if (autoFocus && !assignFieldHandle) {
      setEditing(true);
      if (onAutoFocusRef.current) onAutoFocusRef.current(overlayTarget.current);
    }
  }, [autoFocus, assignFieldHandle]);

  const [isError, setIsError] = useState(false);

  const [errorMessage, showError, flashErrorMessage, cancelFlash] =
    useFlashTransition();

  const submitHandler = async (value, clickAway) => {
    setEditing(false);
    if (!readOnly && value !== stagedValue) {
      setStagedValue(value);
      try {
        setSubmitting(true);
        await onSubmit(value);
        setIsError(false);
      } catch (error) {
        if (clickAway && isError) {
          // When clicking away after two failures, bail on editing
          setIsError(false);
          cancelFlash();
          return;
        }
        setIsError(true);
        const orig = getOriginalError(error);
        const message = orig.fields?.[0]?.value?.[0];

        if (message) {
          setTimeout(() => flashErrorMessage(message));
        }
      } finally {
        setSubmitting(false);
      }
    }
  };

  const handleSubmit = async (value) => {
    await submitHandler(value);
  };

  const handleSubmitOnHide = (value) => {
    submitHandler(value, true);
  };

  const handleOpen = () => {
    setEditing(true);
  };

  const textareaElem = useRef(null);
  const editorRef = useRef(null);

  assignFieldHandle(fieldId, {
    customFocus: () => {
      setEditing(true);
      return overlayTarget.current;
    },
    customEnter: async (e) => {
      const isSubmit = e?.ctrlKey || e?.metaKey;
      if (isSubmit) {
        const editor = editorRef.current?.editor;
        if (editor && !readOnly) {
          const value = await getRawTextValue(editor, isMarkdown);
          handleSubmitOnHide(isMarkdown ? value.trim() : value);
        } else {
          setEditing(false);
        }
        return !shouldFocusNext;
      }
      return true;
    },
    customTab: async () => {
      const editor = editorRef.current?.editor;
      if (editor && !readOnly) {
        const value = await getRawTextValue(editor, isMarkdown);
        handleSubmitOnHide(isMarkdown ? value.trim() : value);
      } else {
        setEditing(false);
      }
    },
    disabled: readOnly,
    shouldFocusNext,
  });

  const { onFocus, onBlur } = getKeyListenersProps?.(fieldId) || {};

  const { urlFromValue, linkButtonProps } = useUrlFromValue(
    value,
    !maskedValueEnabled && !isMarkdown
  );

  return (
    <ViewWrapper
      ref={overlayTarget}
      {...others}
      shrink={!!(urlFromValue || (readOnly && !maskedValueEnabled))}
    >
      <InlineMarkdownText
        isMarkdown={isMarkdown}
        value={stagedValue}
        displayValue={maskedValueEnabled ? processedValue : undefined}
        isOpen={editing}
        onSave={handleSubmit}
        onOpen={handleOpen}
        onHide={handleSubmitOnHide}
        readonly={readOnly}
        onFocus={onFocus}
        onBlur={onBlur}
        editorRef={editorRef}
        inModal={inModal}
        fullTooltip={fullTooltip}
      />
      {urlFromValue ? (
        <LinkButtonWrapper>
          <Button {...linkButtonProps} />
        </LinkButtonWrapper>
      ) : readOnly && value?.trim() ? (
        <LinkButtonWrapper onClick={handleOpen}>
          <IconAdornment icon={'search'} />
        </LinkButtonWrapper>
      ) : null}
      {editing && !submitting ? (
        <Validation
          target={textareaElem.current}
          showMessage={showError}
          message={errorMessage}
        />
      ) : null}
    </ViewWrapper>
  );
}
